import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

type LatLng = { lat: number; lng: number };

type MapPickerProps = {
  value: LatLng | null;
  onChange: (coords: LatLng) => void;
  defaultCenter?: LatLng;
};

const DEFAULT_CENTER: LatLng = { lat: 19.4326, lng: -99.1332 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const MapPicker: React.FC<MapPickerProps> = ({ value, onChange, defaultCenter }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '',
  });

  const [pending, setPending] = useState<LatLng | null>(value);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string>('');
  const hasAutoLocated = useRef(false);

  // Al montar, intentar geolocalización automática una sola vez
  useEffect(() => {
    if (hasAutoLocated.current || value) return;
    hasAutoLocated.current = true;
    if (!('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPending({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        setLocating(false);
        setGeoError('No se pudo obtener tu ubicación automáticamente.');
      },
      { timeout: 10000 },
    );
  }, [value]);

  const center = pending ?? defaultCenter ?? DEFAULT_CENTER;

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    setPending({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const handleMarkerDrag = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    setPending({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const handleConfirm = () => {
    if (pending) onChange(pending);
  };

  if (loadError) {
    return (
      <div className="h-60 flex items-center justify-center text-red-500 text-sm">
        Error al cargar Google Maps.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-60 flex items-center justify-center text-on-surface-variant text-sm">
        Cargando mapa…
      </div>
    );
  }

  const confirmed = value && pending && value.lat === pending.lat && value.lng === pending.lng;

  return (
    <div className="space-y-3">
      {/* Aviso de geolocalización en curso */}
      {locating && (
        <div className="flex items-center gap-2 text-sm text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin text-primary" style={{ fontSize: 18 }}>
            progress_activity
          </span>
          Detectando tu ubicación…
        </div>
      )}
      {geoError && !locating && (
        <p className="text-xs text-amber-600">{geoError}</p>
      )}

      {/* Mapa */}
      <div className="relative h-64 w-full rounded-lg overflow-hidden border border-outline-variant">
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={{ lat: center.lat, lng: center.lng }}
          zoom={pending ? 15 : 5}
          onClick={handleMapClick}
          options={{
            disableDefaultUI: false,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {pending && (
            <Marker
              position={{ lat: pending.lat, lng: pending.lng }}
              draggable
              onDragEnd={handleMarkerDrag}
            />
          )}
        </GoogleMap>
      </div>

      {/* Coordenadas pendientes */}
      {pending && (
        <div className="flex items-center gap-2 px-1">
          <span
            className="material-symbols-outlined text-on-surface-variant"
            style={{ fontVariationSettings: '"FILL" 1', fontSize: 18 }}
          >
            pin_drop
          </span>
          <span className="text-xs text-on-surface-variant font-mono">
            {pending.lat.toFixed(6)}, {pending.lng.toFixed(6)}
          </span>
        </div>
      )}

      {/* Botón confirmar */}
      <button
        type="button"
        disabled={!pending}
        onClick={handleConfirm}
        className={`w-full py-3 rounded-xl font-label-bold text-sm flex items-center justify-center gap-2 transition-all
          ${confirmed
            ? 'bg-green-100 text-green-700 border border-green-400'
            : 'bg-secondary text-on-secondary hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed'
          }`}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontVariationSettings: confirmed ? '"FILL" 1' : '"FILL" 0', fontSize: 20 }}
        >
          {confirmed ? 'check_circle' : 'location_on'}
        </span>
        {confirmed ? 'Ubicación confirmada' : 'Confirmar ubicación'}
      </button>
    </div>
  );
};

export default MapPicker;
